import { useEffect, useState } from "react";
import {
  getMessagingEvents,
  saveMessagingEvent,
  type EventChannels,
  type LifecycleEvent,
} from "../lib/api.ts";

const EVENT_LABELS: Record<LifecycleEvent, { title: string; help: string }> = {
  booking_confirmation: {
    title: "When a customer books",
    help: "The instant after a booking is created — covers both customer-driven bookings on your booking page and staff-created bookings from the dashboard.",
  },
  booking_cancellation: {
    title: "When an appointment is cancelled",
    help: "Fired whether the customer cancels via their cancel link or staff cancel from the dashboard.",
  },
  booking_reminder: {
    title: "Reminder before the appointment",
    help: "Reminder fires once, in the window set by Reminder hours on Settings → Scheduling (default 24h before). Set hours to 0 there to turn the whole event off.",
  },
};

const ORDER: LifecycleEvent[] = ["booking_confirmation", "booking_cancellation", "booking_reminder"];

export function MessagingEvents({ slug }: { slug: string }) {
  const [events, setEvents] = useState<Record<LifecycleEvent, EventChannels> | null>(null);
  const [savingId, setSavingId] = useState<LifecycleEvent | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getMessagingEvents(slug)
      .then((r) => {
        const map = {} as Record<LifecycleEvent, EventChannels>;
        for (const e of r.events) map[e.id] = e;
        setEvents(map);
      })
      .catch((e) => setErr((e as Error).message));
  }, [slug]);

  if (err) return <div className="alert-error">{err}</div>;
  if (!events) return <div style={{ color: "var(--ink-faint)" }}>Loading…</div>;

  const toggle = async (id: LifecycleEvent, channel: "email" | "sms", value: boolean) => {
    const next = { ...events[id], [channel]: value };
    setEvents((s) => (s ? { ...s, [id]: next } : s));
    setSavingId(id);
    try {
      await saveMessagingEvent(slug, id, { email: next.email, sms: next.sms });
    } catch (e) {
      // Revert on failure
      setEvents((s) => (s ? { ...s, [id]: events[id] } : s));
      setErr((e as Error).message);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <section>
      <h2 className="zd-section-title">What to send</h2>
      <p style={{ color: "var(--ink-soft)", fontSize: 14, marginTop: 4, marginBottom: 16 }}>
        For each lifecycle event, pick which channels fire. Off in both columns
        means we don't send anything — useful while you're testing.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(240px, 1fr) 110px 110px",
          gap: 0,
          border: "1px solid var(--rule)",
          borderRadius: 12,
          overflow: "hidden",
          background: "var(--paper-raised)",
        }}
      >
        {/* Header */}
        <div style={headStyle}>Event</div>
        <div style={{ ...headStyle, textAlign: "center" }}>Email</div>
        <div style={{ ...headStyle, textAlign: "center" }}>SMS</div>

        {ORDER.map((id) => {
          const ev = events[id];
          const meta = EVENT_LABELS[id];
          const saving = savingId === id;
          return (
            <ToggleRow
              key={id}
              title={meta.title}
              help={meta.help}
              email={ev.email}
              sms={ev.sms}
              saving={saving}
              onChangeEmail={(v) => toggle(id, "email", v)}
              onChangeSms={(v) => toggle(id, "sms", v)}
            />
          );
        })}
      </div>

      <p style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 10 }}>
        Each toggle saves immediately. SMS only fires for customers whose phone
        number you have on file.
      </p>
    </section>
  );
}

function ToggleRow({
  title, help, email, sms, saving, onChangeEmail, onChangeSms,
}: {
  title: string; help: string;
  email: boolean; sms: boolean; saving: boolean;
  onChangeEmail: (v: boolean) => void;
  onChangeSms: (v: boolean) => void;
}) {
  return (
    <>
      <div style={{ ...cellStyle, paddingTop: 14, paddingBottom: 14, borderTop: "1px solid var(--rule)" }}>
        <div style={{ fontSize: 14, color: "var(--ink)", fontWeight: 500 }}>{title}</div>
        <div style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 4, lineHeight: 1.5 }}>{help}</div>
      </div>
      <ChannelToggle checked={email} onChange={onChangeEmail} disabled={saving} />
      <ChannelToggle checked={sms} onChange={onChangeSms} disabled={saving} />
    </>
  );
}

function ChannelToggle({
  checked, onChange, disabled,
}: { checked: boolean; onChange: (v: boolean) => void; disabled: boolean }) {
  return (
    <label
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        borderTop: "1px solid var(--rule)",
        borderLeft: "1px solid var(--rule)",
        background: "var(--paper-well)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, accentColor: "var(--accent)" }}
      />
    </label>
  );
}

const headStyle: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
  padding: "10px 14px",
  background: "var(--paper-well)",
  borderBottom: "none",
};

const cellStyle: React.CSSProperties = {
  padding: "14px",
};
